import React from 'react';
import { Link } from 'react-router-dom';
import { Lightbulb, Users, Target, TrendingUp, Award } from 'lucide-react';
import './MovementPage.css';

const MovementPage = () => {
  return (
    <div className="movement-page">
      {/* Hero Section */}
      <section className="page-hero movement-hero" data-testid="movement-hero">
        <div className="hero-with-ladders">
          <img 
            src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/kc5vzhwq_laddergirltr.png"
            alt="Learning journey"
            className="hero-ladder hero-ladder-left"
          />
          
          <div className="page-hero-content">
            <div className="hero-icon-wrapper">
              <Users size={80} color="white" className="hero-icon" />
            </div>
            <h1>Lead Change</h1>
            <p>Join the Litvill Social Entrepreneurship Movement and create real community impact</p>
          </div>
          
          <img 
            src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/t4srckf6_ladderboytr.png"
            alt="Learning journey"
            className="hero-ladder hero-ladder-right"
          />
        </div>
      </section>

      {/* Social Entrepreneurship Hub Introduction */}
      <section className="section se-hub-intro">
        <div className="content-wrapper">
          <h2 className="section-title">Social Entrepreneurship Hub</h2>
          <p className="intro-text">
            <strong>Social Entrepreneurship Hub</strong> is an initiative by Litvill, which aims to strengthen 
            Indian universities' efforts to bridge the gap between classroom learning and industry requirements, 
            and execute more impactful community projects.
          </p>
          <div className="stat-highlight">
            <div className="stat-number-large">84%</div>
            <p className="stat-context">
              According to <em>The Economic Times</em>, 84% of corporates believe that university 
              graduates are not ready for the industry. We're addressing this challenge head-on.
            </p>
          </div>
          <p className="intro-text">
            The growing interest in <strong>University Social Responsibility</strong> in India is another 
            motivation for Litvill to initiate the Social Entrepreneurship Hub.
          </p>
        </div>
      </section>

      {/* How We Help Universities */}
      <section className="section university-support" style={{ backgroundColor: 'var(--light-grey)' }}>
        <div className="content-wrapper">
          <h2 className="section-title">Empowering Universities</h2>
          <p className="intro-text">
            Through the Social Entrepreneurship Hub initiative, we enable universities to enhance their 
            ranking in three key areas:
          </p>
          
          <div className="ranking-areas">
            <div className="ranking-card">
              <div className="ranking-number">1</div>
              <h3>Graduate Employability</h3>
              <p>Equipping students with industry-ready skills and practical experience</p>
            </div>
            <div className="ranking-card">
              <div className="ranking-number">2</div>
              <h3>Employer Reputation</h3>
              <p>Building strong connections between universities and industry leaders</p>
            </div>
            <div className="ranking-card">
              <div className="ranking-number">3</div>
              <h3>Social Impact</h3>
              <p>Creating measurable community change through student-led projects</p>
            </div>
          </div>

          <div className="framework-section">
            <h3 className="subsection-title">Our Framework</h3>
            <p className="framework-description">
              We collaborate with universities to establish a social entrepreneurship lab, led by the host 
              university students as a formal company. Under this lab, students run various community projects 
              to uplift children from marginalized communities.
            </p>
            <div className="framework-image-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/ff0d6260_framework.png"
                alt="Social Entrepreneurship Hub Framework"
                className="framework-diagram"
              />
            </div>
          </div>
        </div>
      </section>

      {/* University Lab Handbook Section */}
      <section className="section handbook-section">
        <div className="content-wrapper">
          <h2 className="section-title">University Lab Handbook</h2>
          <div className="handbook-showcase">
            <div className="handbook-cover">
              <img 
                src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/qysl24jo_handbook%20cover.webp"
                alt="Litvill Social Entrepreneurship Lab Handbook"
                className="handbook-image"
              />
            </div>
            <div className="handbook-content">
              <p className="handbook-description">
                We provide comprehensive guidance for universities to establish and operate Litvill Labs. 
                Our detailed handbook includes step-by-step instructions, best practices, curriculum frameworks, 
                and operational guidelines to ensure successful implementation of the Social Entrepreneurship Movement.
              </p>
              <a 
                href="https://www.flipsnack.com/7DDC959BDC9/litvill-se-handbook"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary handbook-btn"
              >
                View Handbook
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* International Advisory Board */}
      <section className="section advisory-board" style={{ backgroundColor: 'var(--light-grey)' }}>
        <div className="content-wrapper">
          <h2 className="section-title">International Central Advisory Board</h2>
          <p className="intro-text">
            All the labs at the host universities are mentored by our <strong>International Central Advisory Board</strong>, 
            empowered by CEOs, CSR managers, university professors, entrepreneurs, and researchers. The key role 
            of the central advisory board is to guide and give feedback to the students to run more impactful 
            projects for higher self-development and social impact.
          </p>
        </div>
      </section>

      {/* Partner Universities */}
      <section className="section partner-universities" style={{ backgroundColor: 'var(--light-grey)' }}>
        <div className="content-wrapper">
          <h2 className="section-title">Our Partner Universities</h2>
          <p className="intro-text">
            We're proud to collaborate with leading institutions across India:
          </p>
          
          <div className="university-logos">
            <div className="logo-card">
              <img 
                src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/bsgubrf6_chitkara2.png"
                alt="Chitkara University"
                className="university-logo"
              />
            </div>
            <div className="logo-card">
              <img 
                src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/0kwu45jt_presidency.png"
                alt="Presidency University"
                className="university-logo"
              />
            </div>
            <div className="logo-card">
              <img 
                src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/lztfhuuw_gibs_logo.png"
                alt="GIBS Business School"
                className="university-logo"
              />
            </div>
            <div className="logo-card">
              <img 
                src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/ku07q2a0_padmashree%20logo.png"
                alt="Padmashree Institute of Management & Sciences"
                className="university-logo"
              />
            </div>
          </div>

          <div className="university-photos">
            <img 
              src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/q1zh6y7b_chitkarasmall.webp"
              alt="Chitkara University partnership"
              className="university-photo"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/dg00aggm_presidencysmall.webp"
              alt="Presidency University collaboration"
              className="university-photo"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/btgtk1dc_gibssmall.webp"
              alt="GIBS Business School activities"
              className="university-photo"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/zldf881o_pimsmall.webp"
              alt="PIMS partnership event"
              className="university-photo"
            />
          </div>

          <p className="counting-text">And Counting...</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section">
        <div className="content-wrapper">
          <h2 className="section-title">The Litvill Movement</h2>
          <p className="intro-text">
            The Litvill Social Entrepreneurship Movement connects passionate young leaders
            across university Labs worldwide. Together, we transform ideas into action,
            learning into leadership, and challenges into opportunities for positive change.
          </p>

          <div className="movement-pillars">
            <div className="pillar-card card" data-testid="pillar-innovation">
              <Lightbulb size={48} color="var(--highlight)" />
              <h3>Innovation</h3>
              <p>
                Develop creative solutions to real-world problems through design thinking
                and social entrepreneurship principles.
              </p>
            </div>

            <div className="pillar-card card" data-testid="pillar-collaboration">
              <Users size={48} color="var(--highlight)" />
              <h3>Collaboration</h3>
              <p>
                Work with diverse teams across universities and communities to amplify
                your impact and learn from each other.
              </p>
            </div>

            <div className="pillar-card card" data-testid="pillar-action">
              <Target size={48} color="var(--highlight)" />
              <h3>Action</h3>
              <p>
                Move from ideas to implementation with structured support, mentorship,
                and resources to bring your projects to life.
              </p>
            </div>

            <div className="pillar-card card" data-testid="pillar-impact">
              <TrendingUp size={48} color="var(--highlight)" />
              <h3>Impact</h3>
              <p>
                Measure and share your results, contributing to a global archive of
                youth-led sustainability innovation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hub Network Section */}
      <section className="section" style={{ backgroundColor: 'var(--light-grey)' }}>
        <div className="content-wrapper">
          <h2 className="section-title">University Lab Network</h2>
          <p className="intro-text">
            Each Litvill Lab is a hub of creativity and action at partner universities.
            Labs organize activities, mentor members, and collaborate with the global network.
          </p>

          <div className="lab-features">
            <div className="lab-feature" data-testid="lab-activity">
              <div className="feature-icon">✓</div>
              <div>
                <h4>Activity Management</h4>
                <p>Plan, propose, and execute sustainability projects with guided support</p>
              </div>
            </div>
            <div className="lab-feature" data-testid="lab-mentorship">
              <div className="feature-icon">✓</div>
              <div>
                <h4>Mentorship</h4>
                <p>Connect with faculty advisors and the Central Advisory Board for guidance</p>
              </div>
            </div>
            <div className="lab-feature" data-testid="lab-recognition">
              <div className="feature-icon">✓</div>
              <div>
                <h4>Recognition</h4>
                <p>Earn digital badges and certificates based on your contributions and impact</p>
              </div>
            </div>
            <div className="lab-feature" data-testid="lab-resources">
              <div className="feature-icon">✓</div>
              <div>
                <h4>Resources</h4>
                <p>Access templates, guides, and best practices from the global network</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="section">
        <div className="content-wrapper">
          <h2 className="section-title">Success Stories</h2>
          <div className="stories-grid">
            <div className="story-card card" data-testid="story-1">
              <Award size={40} color="var(--accent)" />
              <h3>Campus Water Initiative</h3>
              <p className="story-university">Chitkara University Lab</p>
              <p>
                Implemented a campus-wide water conservation program that reduced water
                usage by 30% and educated over 1,000 students about water sustainability.
              </p>
            </div>

            <div className="story-card card" data-testid="story-2">
              <Award size={40} color="var(--accent)" />
              <h3>Community Tutoring Program</h3>
              <p className="story-university">Presidency University Lab</p>
              <p>
                Created an English tutoring program for local underprivileged children,
                reaching 200+ learners and training 50 student mentors.
              </p>
            </div>

            <div className="story-card card" data-testid="story-3">
              <Award size={40} color="var(--accent)" />
              <h3>Zero-Waste Campaign</h3>
              <p className="story-university">Multi-Lab Collaboration</p>
              <p>
                Three university Labs collaborated on a zero-waste awareness campaign,
                organizing events that diverted 500kg of waste from landfills.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="section" style={{ backgroundColor: 'var(--light-grey)' }}>
        <div className="content-wrapper">
          <div className="movement-image-wrapper">
            <img
              src="https://images.unsplash.com/photo-1758270705518-b61b40527e76"
              alt="Students collaborating on projects"
              className="movement-image"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="cta-content">
          <h2>Become a Changemaker</h2>
          <p>
            Whether you're a student looking to start a Lab, a university interested in
            partnership, or a mentor wanting to guide the next generation – we want to hear from you.
          </p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn btn-primary" data-testid="movement-join">
              Join the Movement
            </Link>
            <Link to="/programs" className="btn btn-secondary" data-testid="movement-programs">
              Explore Programs
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MovementPage;